import { InlineSelect, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { getTeamCapabilityTranslationKey, teamCapabilities } from "@msuite/utils/organizations";
import type { TeamFormSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import { useFormContext } from "react-hook-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

export function TeamForm() {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<TeamFormSchema>();
	const { organization } = useAuth();

	/** Query */
	const { data: members } = useQuery({
		queryKey: ["members"],
		queryFn: async () => {
			if (!organization?.id) return null;
			const response = await client.organizations[":orgId"].members.$get({
				param: {
					orgId: organization?.id,
				},
			});
			const data = await responseHandler(response);
			return data.members;
		},
		retry: false,
		staleTime: 1,
	});

	/** Render */
	return (
		<div className="v-stack gap-4">
			<TextInput
				isRequired
				label={t("general.name")}
				control={methods.control}
				name="name"
				placeholder={t("placeholders.team_name")}
			/>
			<InlineSelect
				isRequired={false}
				label={t("organization.team_leaders")}
				control={methods.control}
				name="teamLeaders"
				options={guardArray(members).map((member) => ({
					label: getFullName(member.user),
					value: member.id,
				}))}
			/>
			<InlineSelect
				label={t("organization.team_capabilities")}
				control={methods.control}
				name="capabilities"
				isRequired
				options={teamCapabilities.map((capability) => ({
					label: t(getTeamCapabilityTranslationKey(capability)),
					value: capability,
				}))}
			/>
			<InlineSelect
				isRequired
				label={t("organization.members")}
				control={methods.control}
				name="members"
				options={guardArray(members).map((member) => ({
					label: getFullName(member.user),
					value: member.id,
				}))}
			/>
		</div>
	);
}
